import { ArrowDown, ArrowUpRight, Bell, Monitor, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Section } from "@/components/marketing/Section";
import { Button } from "@/components/ui/Button";
import { DESKTOP_DOWNLOADS, WINDOWS_STORE_URL } from "@/lib/downloads";
import { appUrl } from "@/lib/env";
import { preserveSearch } from "@/lib/nav";
import { useDesktopPlatform } from "@/lib/useDesktopPlatform";

const platforms = [
  { os: "windows", build: "windows", label: "windows.direct" },
  { os: "mac", build: "mac-arm64", label: "mac.appleSilicon" },
  { os: "linux", build: "linux", label: "linux.appImage" },
] as const;
const benefits = [
  { key: "focus", icon: Monitor },
  { key: "notifications", icon: Bell },
  { key: "continuity", icon: RefreshCw },
] as const;

export function Download() {
  const { t } = useTranslation();
  const { search } = useLocation();
  const detected = useDesktopPlatform();
  const primary = platforms.find((platform) => platform.os === detected);
  const others = platforms.filter((platform) => platform !== primary);
  const webUrl = appUrl("/", new URLSearchParams(search).get("ref"));
  const faq = t("download.faq", { returnObjects: true }) as { question: string; answer: string }[];

  return (
    <>
      <Section className="overflow-hidden pb-0 pt-12 lg:pb-0 lg:pt-20">
        <header className="mx-auto max-w-3xl text-center">
          <p className="mb-5 text-sm font-medium text-primary">{t("download.eyebrow")}</p>
          <h1 className="text-balance text-4xl font-semibold leading-[1.06] tracking-tight sm:text-6xl lg:text-7xl">
            {t("download.title")}
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-pretty text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t("download.subtitle")}
          </p>
          <div className="mt-8 flex flex-col items-center gap-3">
            {primary ? (
              <>
                <Button
                  size="lg"
                  className="h-auto min-h-12 w-full max-w-sm whitespace-normal px-5 py-3 text-center text-sm sm:w-auto sm:px-6 sm:text-base"
                  href={DESKTOP_DOWNLOADS[primary.build]}
                  leading={<ArrowDown className="h-4 w-4" aria-hidden />}
                >
                  {t(`download.primary.${primary.os}`)}
                </Button>
                <p className="text-xs text-muted-foreground">
                  {t(`download.requirements.${primary.os}`)}
                </p>
              </>
            ) : (
              <Button
                size="lg"
                className="h-auto min-h-12 w-full max-w-sm whitespace-normal py-3 text-center sm:w-auto"
                href={webUrl}
                trailing={<ArrowUpRight className="h-4 w-4" aria-hidden />}
              >
                {t("download.openWeb")}
              </Button>
            )}
          </div>
          <nav
            className="mx-auto mt-5 flex max-w-sm flex-col gap-2 sm:max-w-none sm:flex-row sm:flex-wrap sm:justify-center"
            aria-label={t("download.otherPlatforms")}
          >
            {others.map((platform) => (
              <Button
                key={platform.os}
                variant="secondary"
                size="sm"
                className="h-auto min-h-11 whitespace-normal py-2 text-center"
                href={DESKTOP_DOWNLOADS[platform.build]}
              >
                {t(`download.${platform.label}`)}
              </Button>
            ))}
            {WINDOWS_STORE_URL ? (
              <Button
                variant="secondary"
                size="sm"
                href={WINDOWS_STORE_URL}
                className="h-auto min-h-11 whitespace-normal py-2 text-center"
              >
                {t("download.windows.store")}
              </Button>
            ) : null}
          </nav>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
            {primary ? (
              <a className="underline-offset-4 hover:text-foreground hover:underline" href={webUrl}>
                {t("download.openWeb")}
              </a>
            ) : null}
            <a
              className="underline-offset-4 hover:text-foreground hover:underline"
              href="https://github.com/Anyrainel/mavori-desktop/releases/latest"
            >
              {t("download.releaseNotes")}
            </a>
            <a
              className="underline-offset-4 hover:text-foreground hover:underline"
              href="#installation"
            >
              {t("download.requirementsLink")}
            </a>
          </div>
        </header>
        <figure className="relative mx-auto mt-12 max-w-5xl sm:mt-16">
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-x-16 top-8 h-3/4 rounded-full bg-primary/10 blur-3xl"
          />
          <div className="relative overflow-hidden rounded-xl border border-white/15 bg-[#101115] shadow-2xl">
            <img
              src="/images/mavori-workspace.png"
              alt={t("download.previewAlt")}
              width={840}
              height={340}
              fetchPriority="high"
              className="block h-auto w-full"
            />
          </div>
          <figcaption className="mt-4 text-center text-xs text-muted-foreground">
            {t("download.previewCaption")}
          </figcaption>
        </figure>
      </Section>
      <Section>
        <ul className="grid gap-9 border-b border-border pb-12 md:grid-cols-3 lg:pb-16">
          {benefits.map(({ key, icon: Icon }) => (
            <li key={key}>
              <Icon className="mb-4 h-5 w-5 text-primary" aria-hidden />
              <h2 className="text-lg font-semibold">{t(`desktopIntro.${key}.title`)}</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {t(`desktopIntro.${key}.body`)}
              </p>
            </li>
          ))}
        </ul>
        <div
          id="installation"
          className="grid scroll-mt-24 gap-8 pt-12 md:grid-cols-[1fr_1.5fr] lg:pt-16"
        >
          <div>
            <h2 className="text-3xl font-semibold tracking-tight">{t("download.setupTitle")}</h2>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground">
              {t("download.setupBody")}
            </p>
            <Button
              variant="link"
              to={preserveSearch("/docs/desktop", search)}
              className="mt-4 px-0"
              trailing={<ArrowUpRight className="h-4 w-4" aria-hidden />}
            >
              {t("download.help")}
            </Button>
          </div>
          <div className="divide-y divide-border border-y border-border">
            {faq.map(({ question, answer }) => (
              <details key={question} className="group py-5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium [&::-webkit-details-marker]:hidden">
                  {question}
                  <span aria-hidden className="text-xl text-muted-foreground group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </Section>
    </>
  );
}
